(() => {
  const runtimeScript = document.currentScript;
  const nativeFontsUrl = runtimeScript?.dataset.c3NativeFontsUrl;
  const existingRuntime = window.__c3NativeSectionsRuntime;

  if (existingRuntime) {
    existingRuntime.registerSectionAssets(nativeFontsUrl);
    existingRuntime.scheduleNativeFonts();
    existingRuntime.scheduleLazyPictures();
    return;
  }

  const runtime = {
    nativeFontsUrl,
    fontsLoaded: false,
    fontsTarget: null,
    fontsObserver: null,
    fontListenersBound: false,
    pictureObserver: null,
    registerSectionAssets(fontsUrl) {
      if (fontsUrl) this.nativeFontsUrl = fontsUrl;
    },
  };

  const loadNativeFonts = () => {
    if (!runtime.nativeFontsUrl) return;

    const existingStylesheet = document.getElementById('C3NativeFonts');
    if (existingStylesheet) {
      runtime.fontsLoaded = true;
      runtime.fontsObserver?.disconnect();
      runtime.fontsTarget = null;
      return;
    }

    const stylesheet = document.createElement('link');
    stylesheet.id = 'C3NativeFonts';
    stylesheet.rel = 'stylesheet';
    stylesheet.href = runtime.nativeFontsUrl;
    document.head.appendChild(stylesheet);

    runtime.fontsLoaded = true;
    runtime.fontsObserver?.disconnect();
    runtime.fontsTarget = null;
  };

  const scheduleNativeFonts = () => {
    if (runtime.fontsLoaded) return;

    const firstNativeSection = document.querySelector('.c3-native');
    if (!firstNativeSection) return;

    window.__c3LoadNativeFonts = loadNativeFonts;

    if ((window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) {
      loadNativeFonts();
      return;
    }

    if (!runtime.fontListenersBound) {
      window.addEventListener('scroll', loadNativeFonts, { once: true, passive: true });
      ['pointerenter', 'pointerdown', 'touchstart', 'focusin'].forEach((eventName) => {
        document.addEventListener(eventName, loadNativeFonts, { once: true, passive: true, capture: true });
      });
      runtime.fontListenersBound = true;
    }

    if (!runtime.fontsObserver) {
      runtime.fontsObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          loadNativeFonts();
        },
        { rootMargin: '0px' }
      );
    }

    if (runtime.fontsTarget && runtime.fontsTarget !== firstNativeSection) {
      runtime.fontsObserver.unobserve(runtime.fontsTarget);
    }

    runtime.fontsTarget = firstNativeSection;
    runtime.fontsObserver.observe(firstNativeSection);
  };

  const hydrateLazyPicture = (picture) => {
    if (!picture || picture.dataset.c3Hydrated === 'true') return;

    picture.querySelectorAll('source[data-c3-native-lazy-source]').forEach((source) => {
      if (source.dataset.srcset) {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      }
      if (source.dataset.src) {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      }
      source.removeAttribute('data-c3-native-lazy-source');
    });

    const img = picture.querySelector('img[data-c3-native-lazy-image]');
    if (img) {
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
      img.removeAttribute('data-c3-native-lazy-image');
    }

    picture.removeAttribute('data-c3-native-observing');
    picture.dataset.c3Hydrated = 'true';
  };

  const scheduleLazyPictures = () => {
    const pictures = Array.from(document.querySelectorAll('[data-c3-native-lazy-picture]')).filter(
      (picture) =>
        picture.dataset.c3Hydrated !== 'true' && picture.dataset.c3NativeObserving !== 'true'
    );
    if (!pictures.length) return;

    if ((window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) {
      pictures.forEach((picture) => hydrateLazyPicture(picture));
      return;
    }

    if (!runtime.pictureObserver) {
      runtime.pictureObserver = new IntersectionObserver(
        (entries, currentObserver) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            hydrateLazyPicture(entry.target);
            currentObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '400px 0px' }
      );
    }

    pictures.forEach((picture) => {
      picture.dataset.c3NativeObserving = 'true';
      runtime.pictureObserver.observe(picture);
    });
  };

  runtime.scheduleNativeFonts = scheduleNativeFonts;
  runtime.scheduleLazyPictures = scheduleLazyPictures;
  window.__c3NativeSectionsRuntime = runtime;

  scheduleNativeFonts();
  scheduleLazyPictures();

  class C3AudioDemo extends HTMLElement {
    constructor() {
      super();
      this.audio = null;
      this.activeButton = null;
      this.abortController = null;
      this.videoObserver = null;
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      this.querySelectorAll('[data-c3-audio-button]').forEach((button) => {
        button.addEventListener('click', () => this.toggleAudio(button), {
          signal: this.abortController.signal,
        });
      });

      this.prepareVideos();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.videoObserver?.disconnect();
      this.stopAudio();
    }

    toggleAudio(button) {
      if (this.activeButton === button && this.audio) {
        this.stopAudio();
        this.announce('Audio stopped');
        return;
      }

      const url = button.dataset.audioUrl;
      if (!url) return;

      this.stopAudio();
      this.audio = new Audio(url);
      this.activeButton = button;
      this.setPressedButton(button);
      this.announce(`Playing ${button.textContent.trim()}`);

      const reset = () => {
        if (this.activeButton !== button) return;
        this.setPressedButton(null);
        this.activeButton = null;
        this.audio = null;
        this.announce('Audio stopped');
      };

      this.audio.addEventListener('ended', reset, { once: true });
      this.audio.addEventListener('error', reset, { once: true });
      this.audio.play().catch(reset);
    }

    stopAudio() {
      if (this.audio) {
        this.audio.pause();
        this.audio.currentTime = 0;
      }
      this.audio = null;
      this.activeButton = null;
      this.setPressedButton(null);
    }

    setPressedButton(activeButton) {
      this.querySelectorAll('[data-c3-audio-button]').forEach((button) => {
        button.setAttribute('aria-pressed', button === activeButton ? 'true' : 'false');
      });
    }

    announce(message) {
      const status = this.querySelector('[data-c3-audio-status]');
      if (status) status.textContent = message;
    }

    prepareVideos() {
      const videos = Array.from(this.querySelectorAll('[data-c3-lazy-video]'));
      if (!videos.length) return;

      if (!('IntersectionObserver' in window)) {
        videos.forEach((video) => this.hydrateVideo(video));
        return;
      }

      this.videoObserver?.disconnect();
      this.videoObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.hydrateVideo(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '320px 0px' }
      );

      videos.forEach((video) => this.videoObserver.observe(video));
    }

    hydrateVideo(video) {
      if (video.dataset.c3Hydrated === 'true') return;

      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.c3Hydrated = 'true';
      video.load();

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (video.hasAttribute('autoplay') && !reducedMotion) {
        video.play().catch(() => {});
      }
    }
  }

  class C3MediaRail extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.track = this.querySelector('[data-c3-rail-track]');
      this.previousButton = this.querySelector('[data-c3-rail-previous]');
      this.nextButton = this.querySelector('[data-c3-rail-next]');
      if (!this.track) return;

      this.previousButton?.addEventListener('click', () => this.move(-1), {
        signal: this.abortController.signal,
      });
      this.nextButton?.addEventListener('click', () => this.move(1), {
        signal: this.abortController.signal,
      });
      this.track.addEventListener('scroll', () => this.updateButtons(), {
        passive: true,
        signal: this.abortController.signal,
      });

      this.resizeObserver?.disconnect();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateButtons());
        this.resizeObserver.observe(this.track);
      }

      requestAnimationFrame(() => this.updateButtons());
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
    }

    move(direction) {
      const item = this.track.firstElementChild;
      if (!item) return;
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: direction * (item.getBoundingClientRect().width + gap),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    updateButtons() {
      if (!this.track) return;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.abs(this.track.scrollLeft);
      if (this.previousButton) this.previousButton.disabled = position <= 2;
      if (this.nextButton) this.nextButton.disabled = position >= maximum - 2;
    }
  }

  if (!customElements.get('c3-audio-demo')) {
    customElements.define('c3-audio-demo', C3AudioDemo);
  }
  if (!customElements.get('c3-media-rail')) {
    customElements.define('c3-media-rail', C3MediaRail);
  }
})();
